import { getBuiltinDesignExtensionPreset } from '@/lib/builtin-design-templates';
import { getNodePreviewImageUrl, getNodePrimaryMediaUrl } from '@/lib/image-utils';
import {
  MODEL_STORYBOARD_VIDEO_PROMPT_BUILDER,
  PRODUCT_STORYBOARD_VIDEO_PROMPT_BUILDER,
  getTemplatePromptBuilderFromModelParams,
} from '@/lib/template-text-prompt-slots';
import {
  DEFAULT_TEMPLATE_OUTPUT_COUNT_OPTIONS,
  clampTemplateOutputCountValue,
  resolveTemplateDefaultOutputCount,
  resolveTemplateOutputCountOptions,
} from '@/lib/template-output-count-config';
import type { CanvasNode, CanvasTemplateInstance, CanvasTemplateSlotValue } from '@/types/visual-editor';
import type { ImageTemplate, InputSlot, TemplateVariant } from '@/types/image-template';

export const TEMPLATE_IMAGE_COUNT_OPTIONS = DEFAULT_TEMPLATE_OUTPUT_COUNT_OPTIONS;
export const TEMPLATE_NODE_FOOTPRINT = {
  width: 440,
  height: 560,
} as const;
export const TEMPLATE_GRID_RATIO_OPTIONS = ['1:1', '4:5', '3:4'] as const;
const BRAND_SHOOT_DELIVERY_PROMPT_BUILDER = 'brand_shoot_delivery';
const WHITE_BACKGROUND_DELIVERY_PROMPT_BUILDER = 'white_background_delivery';
const BUILTIN_VIDEO_STORYBOARD_TEMPLATE_KIND = 'video_storyboard_breakdown';
const PRODUCT_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND = 'product_storyboard_video_stage';
const MODEL_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND = 'model_storyboard_video_stage';
const STORYBOARD_STAGE_SOURCE_SLOT_KEY = 'source_image';
const STORYBOARD_STAGE_STORYBOARD_SLOT_KEY = 'storyboard_image';
const TEMPLATE_GRID_OPTIONS = [
  { rows: 2, cols: 2 },
  { rows: 3, cols: 3 },
  { rows: 4, cols: 4 },
] as const;

const STORYBOARD_STAGE_SOURCE_SLOT_KEYS_BY_CAPABILITY: Record<string, string[]> = {
  [PRODUCT_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND]: ['product_image', 'source_image', 'subject_image', 'model_image'],
  [MODEL_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND]: ['model_image', 'source_image', 'subject_image', 'product_image'],
};

export type TemplatePromptLocale = 'en' | 'zh';
export type LocalizedTemplatePromptTexts = Partial<Record<TemplatePromptLocale, string>>;

export interface TemplateRunRequest {
  userImages: string[];
  userParams: Record<string, unknown>;
}

export interface TemplateRunValidationResult {
  ok: boolean;
  missingSlots: InputSlot[];
  unresolvedWorkflowBindingSlots: InputSlot[];
  request?: TemplateRunRequest;
}

interface ValidateTemplateRunRequestOptions {
  allowUnresolvedWorkflowBindings?: boolean;
}

export interface ResolvedTemplateSlotMedia {
  sourceUrl: string;
  previewUrl?: string;
  sourceNodeId?: string;
  assetId?: string | null;
  durationSeconds?: number | null;
}

function getNodeDurationSeconds(node: CanvasNode | null | undefined): number | null {
  const duration = Number(node?.duration);
  return Number.isFinite(duration) && duration > 0 ? duration : null;
}

interface StoryboardStageResolvedMedia {
  sourceImageUrl?: string;
  storyboardImageUrl?: string;
  promotedStoryboardSlotKey?: string | null;
}

function normalizeAutoSplitConfig(value: unknown): { rows: number; cols: number } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const rows = Number((value as { rows?: unknown }).rows);
  const cols = Number((value as { cols?: unknown }).cols);
  if (!Number.isFinite(rows) || rows < 1 || !Number.isFinite(cols) || cols < 1) {
    return null;
  }
  return {
    rows: Math.round(rows),
    cols: Math.round(cols),
  };
}

function normalizeAutoSplitOptions(value: unknown): Array<{ rows: number; cols: number }> {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const options: Array<{ rows: number; cols: number }> = [];
  value.forEach((item) => {
    const option = normalizeAutoSplitConfig(item);
    if (!option) {
      return;
    }
    const key = `${option.rows}x${option.cols}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    options.push(option);
  });
  return options;
}

function getPromptBuilderFromModelParams(modelParams: Record<string, unknown>): string {
  return getTemplatePromptBuilderFromModelParams(modelParams);
}

function getTemplateDefaultOutputCount(template: Pick<ImageTemplate, 'id' | 'output_type' | 'model_params' | 'capability'>): number {
  const promptBuilder = getPromptBuilderFromModelParams(template.model_params || {});
  if (
    template.output_type === 'video'
    || template.capability === BUILTIN_VIDEO_STORYBOARD_TEMPLATE_KIND
    || Boolean(getBuiltinDesignExtensionPreset(template.id))
    || promptBuilder === PRODUCT_STORYBOARD_VIDEO_PROMPT_BUILDER
    || promptBuilder === MODEL_STORYBOARD_VIDEO_PROMPT_BUILDER
  ) {
    return 1;
  }

  return resolveTemplateDefaultOutputCount(template);
}

function getVariantDefinition(variants: TemplateVariant[] | undefined, selectedVariantKey?: string | null): TemplateVariant | null {
  if (!selectedVariantKey) {
    return null;
  }
  return variants?.find(item => item.key === selectedVariantKey) ?? null;
}

function normalizeLocalizedTemplatePromptText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().replace(/\r\n/g, '\n');
  return normalized.length > 0 ? normalized : undefined;
}

function asTemplateRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function readLocalizedPromptTextsFromRecord(record: Record<string, unknown> | null | undefined): LocalizedTemplatePromptTexts | null {
  if (!record) {
    return null;
  }

  const direct = {
    en: normalizeLocalizedTemplatePromptText(record.en),
    zh: normalizeLocalizedTemplatePromptText(record.zh),
  };
  if (direct.en || direct.zh) {
    return direct;
  }

  for (const key of [
    'localized_prompt_templates',
    'localizedPromptTemplates',
    'localized_prompt_texts',
    'localizedPromptTexts',
    'localized_prompt_overrides',
    'localizedPromptOverrides',
    'prompt_templates',
    'promptTemplates',
    'prompt_template_i18n',
    'promptTemplateI18n',
    'prompt_overrides',
    'promptOverrides',
  ]) {
    const nested = asTemplateRecord(record[key]);
    const localized = readLocalizedPromptTextsFromRecord(nested);
    if (localized) {
      return localized;
    }
  }

  const en = normalizeLocalizedTemplatePromptText(record.prompt_template_en)
    || normalizeLocalizedTemplatePromptText(record.promptTemplateEn)
    || normalizeLocalizedTemplatePromptText(record.prompt_en)
    || normalizeLocalizedTemplatePromptText(record.promptEn);
  const zh = normalizeLocalizedTemplatePromptText(record.prompt_template_zh)
    || normalizeLocalizedTemplatePromptText(record.promptTemplateZh)
    || normalizeLocalizedTemplatePromptText(record.prompt_zh)
    || normalizeLocalizedTemplatePromptText(record.promptZh);

  return en || zh ? { en, zh } : null;
}

export function mergeLocalizedTemplatePromptTexts(
  ...sources: Array<LocalizedTemplatePromptTexts | null | undefined>
): LocalizedTemplatePromptTexts | undefined {
  const merged: LocalizedTemplatePromptTexts = {};

  sources.forEach((source) => {
    if (!source) {
      return;
    }
    const en = normalizeLocalizedTemplatePromptText(source.en);
    const zh = normalizeLocalizedTemplatePromptText(source.zh);
    if (en) {
      merged.en = en;
    }
    if (zh) {
      merged.zh = zh;
    }
  });

  return merged.en || merged.zh ? merged : undefined;
}

export function resolveTemplatePromptLocale(locale?: string | null): TemplatePromptLocale {
  return typeof locale === 'string' && locale.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

function resolveTemplateLocalizedPromptTemplates(
  template: Pick<ImageTemplate, 'prompt_template' | 'prompt_template_en' | 'prompt_template_zh' | 'localized_prompt_templates' | 'model_params' | 'variants'>,
  selectedVariantKey?: string | null,
): LocalizedTemplatePromptTexts | undefined {
  const templateRecord = template as unknown as Record<string, unknown>;
  const modelParamsRecord = asTemplateRecord(template.model_params);
  const variant = getVariantDefinition(template.variants, selectedVariantKey);
  const variantRecord = variant as unknown as Record<string, unknown> | null;
  const variantModelParamsRecord = asTemplateRecord(variant?.model_params_override);

  return mergeLocalizedTemplatePromptTexts(
    {
      en: normalizeLocalizedTemplatePromptText(template.prompt_template_en) || normalizeLocalizedTemplatePromptText(template.prompt_template),
      zh: normalizeLocalizedTemplatePromptText(template.prompt_template_zh),
    },
    readLocalizedPromptTextsFromRecord(templateRecord),
    readLocalizedPromptTextsFromRecord(modelParamsRecord),
    variant
      ? {
          en: normalizeLocalizedTemplatePromptText(variant.prompt_override_en) || normalizeLocalizedTemplatePromptText(variant.prompt_override),
          zh: normalizeLocalizedTemplatePromptText(variant.prompt_override_zh),
        }
      : null,
    readLocalizedPromptTextsFromRecord(variantRecord),
    readLocalizedPromptTextsFromRecord(variantModelParamsRecord),
  );
}

export function localizeTemplatePromptText(
  promptText: string | undefined,
  localizedPromptTexts: LocalizedTemplatePromptTexts | null | undefined,
  locale: string | null | undefined,
): string {
  const normalizedPromptText = normalizeLocalizedTemplatePromptText(promptText) || '';
  const localizedTexts = mergeLocalizedTemplatePromptTexts(localizedPromptTexts);
  if (!localizedTexts) {
    return normalizedPromptText;
  }

  const promptLocale = resolveTemplatePromptLocale(locale);
  const preferredText = localizedTexts[promptLocale];
  const alternateText = promptLocale === 'zh' ? localizedTexts.en : localizedTexts.zh;
  const knownSourceText = normalizedPromptText === localizedTexts.en || normalizedPromptText === localizedTexts.zh;

  if (!normalizedPromptText || knownSourceText) {
    return preferredText || alternateText || normalizedPromptText;
  }

  return normalizedPromptText;
}

export function getCanvasTemplateInstanceLocalizedPromptTemplates(
  instance: Pick<CanvasTemplateInstance, 'basePromptTemplate' | 'promptTemplate' | 'localizedPromptTemplates'>,
): LocalizedTemplatePromptTexts | undefined {
  const localizedPromptTemplates = mergeLocalizedTemplatePromptTexts(instance.localizedPromptTemplates);
  const basePromptTemplate = normalizeLocalizedTemplatePromptText(instance.basePromptTemplate);
  const currentPromptTemplate = normalizeLocalizedTemplatePromptText(instance.promptTemplate);
  const currentPromptTemplateIsKnownZh = Boolean(
    localizedPromptTemplates?.zh
    && currentPromptTemplate === localizedPromptTemplates.zh,
  );

  return mergeLocalizedTemplatePromptTexts(
    localizedPromptTemplates,
    {
      en: localizedPromptTemplates?.en
        ? undefined
        : basePromptTemplate || (currentPromptTemplateIsKnownZh ? undefined : currentPromptTemplate),
    },
  );
}

export function localizeCanvasTemplateDefaultPrompt(
  instance: Pick<CanvasTemplateInstance, 'basePromptTemplate' | 'promptTemplate' | 'localizedPromptTemplates'>,
  locale: string | null | undefined,
): string {
  return localizeTemplatePromptText(
    instance.promptTemplate,
    getCanvasTemplateInstanceLocalizedPromptTemplates(instance),
    locale,
  );
}

function resolveTemplatePromptTemplate(
  basePromptTemplate: string | undefined,
  variants: TemplateVariant[] | undefined,
  selectedVariantKey?: string | null,
  localizedPromptTexts?: LocalizedTemplatePromptTexts | null,
): string {
  const variant = getVariantDefinition(variants, selectedVariantKey);
  const variantRecord = variant as unknown as Record<string, unknown> | null;
  const variantModelParamsRecord = asTemplateRecord(variant?.model_params_override);
  const variantLocalizedPromptTexts = variant
    ? mergeLocalizedTemplatePromptTexts(
        {
          en: normalizeLocalizedTemplatePromptText(variant.prompt_override_en) || normalizeLocalizedTemplatePromptText(variant.prompt_override),
          zh: normalizeLocalizedTemplatePromptText(variant.prompt_override_zh),
        },
        readLocalizedPromptTextsFromRecord(variantRecord),
        readLocalizedPromptTextsFromRecord(variantModelParamsRecord),
      )
    : undefined;
  const variantPromptOverride = normalizeLocalizedTemplatePromptText(variant?.prompt_override)
    || variantLocalizedPromptTexts?.en
    || variantLocalizedPromptTexts?.zh;
  if (variantPromptOverride) {
    return variantPromptOverride;
  }

  const basePrompt = normalizeLocalizedTemplatePromptText(basePromptTemplate);
  if (basePrompt) {
    return basePrompt;
  }

  const localizedPrompt = mergeLocalizedTemplatePromptTexts(localizedPromptTexts);
  return localizedPrompt?.en || localizedPrompt?.zh || '';
}

function getTemplateSlotsForVariant(
  inputSlots: InputSlot[],
  variants: TemplateVariant[] | undefined,
  selectedVariantKey?: string | null,
): InputSlot[] {
  return getVariantDefinition(variants, selectedVariantKey)?.input_slots_override || inputSlots || [];
}

function getTemplateRuntimeSlots(
  inputSlots: InputSlot[],
  variants: TemplateVariant[] | undefined,
  selectedVariantKey: string | null | undefined,
  modelParams: Record<string, unknown>,
): InputSlot[] {
  void modelParams;
  return getTemplateSlotsForVariant(inputSlots, variants, selectedVariantKey);
}

function shouldAutoSelectFirstVariant(template: ImageTemplate): boolean {
  if (!template.variants?.length) {
    return false;
  }
  const promptBuilder = getPromptBuilderFromModelParams(template.model_params || {});
  return promptBuilder !== WHITE_BACKGROUND_DELIVERY_PROMPT_BUILDER
    && promptBuilder !== BRAND_SHOOT_DELIVERY_PROMPT_BUILDER;
}

function getInitialTemplateVariantKey(template: ImageTemplate): string | null {
  if (!shouldAutoSelectFirstVariant(template)) {
    return null;
  }
  return template.variants?.[0]?.key ?? null;
}

function cloneGridConfig(value: { rows: number; cols: number }): { rows: number; cols: number } {
  return {
    rows: value.rows,
    cols: value.cols,
  };
}

function buildTemplateDefaultValues(slots: InputSlot[]): CanvasTemplateInstance['values'] {
  const values: CanvasTemplateInstance['values'] = {};
  slots.forEach((slot) => {
    const defaultValue = buildDefaultTemplateSlotValue(slot);
    if (defaultValue) {
      values[slot.key] = defaultValue;
    }
  });
  return values;
}

function resolveSelectSlotDefaultValue(slot: Pick<InputSlot, 'default_value' | 'options'>): string | undefined {
  return slot.default_value || slot.options?.[0]?.value;
}

function buildDefaultTemplateSlotValue(slot: InputSlot): CanvasTemplateSlotValue | null {
  if (slot.type === 'select') {
    const selectValue = resolveSelectSlotDefaultValue(slot);
    return selectValue ? { selectValue } : null;
  }

  if (slot.type === 'text' && slot.default_value) {
    return { text: slot.default_value };
  }

  return null;
}

function cloneTemplateSlotValue(value: CanvasTemplateSlotValue): CanvasTemplateSlotValue {
  return {
    sourceUrl: value.sourceUrl,
    previewUrl: value.previewUrl,
    sourceNodeId: value.sourceNodeId,
    assetId: value.assetId,
    text: value.text,
    selectValue: value.selectValue,
    workflowBinding: value.workflowBinding
      ? { ...value.workflowBinding }
      : value.workflowBinding,
  };
}

function isTemplatePlaceholderMediaValue(slot: InputSlot, value: CanvasTemplateSlotValue | undefined): boolean {
  if (!value || !isMediaSlot(slot.type)) {
    return false;
  }

  const defaultUrl = slot.default_url?.trim();
  const sourceUrl = value.sourceUrl?.trim();
  return !!defaultUrl
    && !!sourceUrl
    && sourceUrl === defaultUrl
    && !value.sourceNodeId
    && !value.workflowBinding
    && !value.assetId;
}

function sanitizeTemplateInstanceValues(instance: CanvasTemplateInstance): CanvasTemplateInstance['values'] {
  const slotMap = new Map(
    getTemplateRuntimeSlots(instance.inputSlots, instance.variants, instance.selectedVariantKey, getTemplateInstanceModelParams(instance))
      .map((slot) => [slot.key, slot]),
  );
  const nextValues: CanvasTemplateInstance['values'] = {};

  Object.entries(instance.values || {}).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    const slot = slotMap.get(key);
    if (slot && isTemplatePlaceholderMediaValue(slot, value)) {
      return;
    }

    nextValues[key] = cloneTemplateSlotValue(value);
  });

  return nextValues;
}

function isMediaSlot(type: InputSlot['type']): type is 'image' | 'video' {
  return type === 'image' || type === 'video';
}

function reconcileTemplateSlotValue(
  nextSlot: InputSlot,
  previousSlot: InputSlot | undefined,
  previousValue: CanvasTemplateSlotValue | undefined,
): CanvasTemplateSlotValue | null {
  const nextDefault = buildDefaultTemplateSlotValue(nextSlot);
  if (!previousValue || !previousSlot) {
    return nextDefault;
  }

  if (isMediaSlot(nextSlot.type) && isMediaSlot(previousSlot.type)) {
    const workflowBinding = previousValue.workflowBinding
      ? { ...previousValue.workflowBinding }
      : undefined;
    const sourceUrl = previousValue.sourceUrl?.trim();
    if (!sourceUrl && !previousValue.sourceNodeId && !workflowBinding) {
      return nextDefault;
    }

    const previousDefault = previousSlot.default_url?.trim();
    const previousWasTemplateDefault = !previousValue.sourceNodeId
      && !workflowBinding
      && !previousValue.assetId
      && !!previousDefault
      && sourceUrl === previousDefault;

    if (previousWasTemplateDefault) {
      return nextDefault;
    }

    return {
      ...(sourceUrl ? {
        sourceUrl,
        previewUrl: previousValue.previewUrl || sourceUrl,
      } : {}),
      ...(previousValue.sourceNodeId ? { sourceNodeId: previousValue.sourceNodeId } : {}),
      ...(previousValue.assetId !== undefined ? { assetId: previousValue.assetId } : {}),
      ...(workflowBinding ? { workflowBinding } : {}),
    };
  }

  if (nextSlot.type === 'select' && previousSlot.type === 'select') {
    const nextAllowedValues = new Set((nextSlot.options || []).map((option) => option.value));
    const previousSelected = previousValue.selectValue?.trim();
    const previousDefault = resolveSelectSlotDefaultValue(previousSlot);
    const nextDefaultValue = resolveSelectSlotDefaultValue(nextSlot);

    if (previousSelected && previousDefault && previousSelected === previousDefault && nextDefaultValue && previousSelected !== nextDefaultValue) {
      return { selectValue: nextDefaultValue };
    }

    if (previousSelected && nextAllowedValues.has(previousSelected)) {
      return { selectValue: previousSelected };
    }

    return nextDefault;
  }

  if (nextSlot.type === 'text' && previousSlot.type === 'text') {
    const previousText = previousValue.text ?? '';
    const previousDefault = previousSlot.default_value ?? '';
    const nextDefaultText = nextSlot.default_value ?? '';
    const previousWasTemplateDefault = previousText.length > 0 && previousText === previousDefault;
    const nextValue: CanvasTemplateSlotValue = {};

    if (previousWasTemplateDefault && nextDefaultText.length > 0 && previousText !== nextDefaultText) {
      nextValue.text = nextDefaultText;
    } else if (previousText.length > 0) {
      nextValue.text = previousText;
    } else if (nextDefault?.text) {
      nextValue.text = nextDefault.text;
    }

    if (previousValue.sourceNodeId) {
      nextValue.sourceNodeId = previousValue.sourceNodeId;
    }

    if (previousValue.workflowBinding) {
      nextValue.workflowBinding = { ...previousValue.workflowBinding };
    }

    return Object.keys(nextValue).length > 0 ? nextValue : nextDefault;
  }

  return nextDefault;
}

export function createCanvasTemplateInstance(template: ImageTemplate): CanvasTemplateInstance {
  const now = new Date().toISOString();
  const selectedVariantKey = getInitialTemplateVariantKey(template);
  const runtimeSlots = getTemplateRuntimeSlots(template.input_slots, template.variants, selectedVariantKey, template.model_params || {});
  const values = buildTemplateDefaultValues(runtimeSlots);
  const outputCount = getTemplateDefaultOutputCount(template);
  const localizedPromptTemplates = resolveTemplateLocalizedPromptTemplates(template, selectedVariantKey);
  const promptTemplate = resolveTemplatePromptTemplate(
    template.prompt_template,
    template.variants,
    selectedVariantKey,
    localizedPromptTemplates,
  );

  return normalizeCanvasTemplateInstance({
    templateId: template.id,
    templateUpdatedAt: template.updated_at,
    name: template.name,
    nameZh: template.name_zh,
    coverUrl: template.cover_url,
    outputType: template.output_type,
    capability: template.capability,
    basePromptTemplate: template.prompt_template,
    promptTemplate,
    localizedPromptTemplates,
    promptSourceNodeId: null,
    modelParams: template.model_params || {},
    inputSlots: template.input_slots,
    variants: template.variants,
    selectedVariantKey,
    values,
    params: {},
    outputCount,
    activeRun: null,
    runHistory: [],
    createdAt: now,
    updatedAt: now,
  });
}

export function createCanvasTemplateInstanceFromTemplateSnapshot(
  template: Pick<
    CanvasTemplateInstance,
    'templateId' | 'templateUpdatedAt' | 'name' | 'nameZh' | 'coverUrl' | 'outputType' | 'capability' | 'basePromptTemplate' | 'localizedPromptTemplates' | 'modelParams' | 'inputSlots' | 'variants'
  >,
): CanvasTemplateInstance {
  const instance = createCanvasTemplateInstance({
    id: template.templateId,
    updated_at: template.templateUpdatedAt || new Date().toISOString(),
    name: template.name,
    name_zh: template.nameZh,
    cover_url: template.coverUrl || null,
    output_type: template.outputType,
    capability: template.capability,
    prompt_template: template.basePromptTemplate || '',
    localized_prompt_templates: template.localizedPromptTemplates ?? undefined,
    model_params: template.modelParams || {},
    input_slots: template.inputSlots || [],
    variants: template.variants || [],
  } as ImageTemplate);

  return normalizeCanvasTemplateInstance({
    ...instance,
    localizedPromptTemplates: template.localizedPromptTemplates ?? instance.localizedPromptTemplates,
  });
}

function buildTemplateSyncSignatureFromInstance(instance: Pick<
  CanvasTemplateInstance,
  'name' | 'nameZh' | 'coverUrl' | 'outputType' | 'capability' | 'basePromptTemplate' | 'localizedPromptTemplates' | 'modelParams' | 'inputSlots' | 'variants'
>): string {
  return JSON.stringify({
    name: instance.name,
    nameZh: instance.nameZh ?? null,
    coverUrl: instance.coverUrl ?? null,
    outputType: instance.outputType,
    capability: instance.capability,
    basePromptTemplate: instance.basePromptTemplate ?? '',
    localizedPromptTemplates: instance.localizedPromptTemplates ?? null,
    modelParams: instance.modelParams || {},
    inputSlots: instance.inputSlots || [],
    variants: instance.variants || [],
  });
}

function buildTemplateSyncSignatureFromTemplate(template: Pick<
  ImageTemplate,
  'name' | 'name_zh' | 'cover_url' | 'output_type' | 'capability' | 'prompt_template' | 'prompt_template_en' | 'prompt_template_zh' | 'localized_prompt_templates' | 'model_params' | 'input_slots' | 'variants'
>): string {
  return JSON.stringify({
    name: template.name,
    nameZh: template.name_zh ?? null,
    coverUrl: template.cover_url ?? null,
    outputType: template.output_type,
    capability: template.capability,
    basePromptTemplate: template.prompt_template ?? '',
    localizedPromptTemplates: resolveTemplateLocalizedPromptTemplates(template) ?? null,
    modelParams: template.model_params || {},
    inputSlots: template.input_slots || [],
    variants: template.variants || [],
  });
}

export function syncCanvasTemplateInstanceWithTemplate(
  instance: CanvasTemplateInstance,
  template: ImageTemplate,
): { instance: CanvasTemplateInstance; changed: boolean } {
  const templateUpdatedAt = template.updated_at || undefined;
  const templateMatchesSnapshot = buildTemplateSyncSignatureFromInstance(instance)
    === buildTemplateSyncSignatureFromTemplate(template);

  if (instance.templateUpdatedAt === templateUpdatedAt && templateMatchesSnapshot) {
    return { instance, changed: false };
  }

  const previousSlots = getTemplateRuntimeSlots(instance.inputSlots, instance.variants, instance.selectedVariantKey, getTemplateInstanceModelParams(instance));
  const previousSlotMap = new Map(previousSlots.map((slot) => [slot.key, slot]));
  const nextValues: CanvasTemplateInstance['values'] = {};

  const nextSelectedVariantKey = instance.selectedVariantKey
    && template.variants.some((variant) => variant.key === instance.selectedVariantKey)
    ? instance.selectedVariantKey
    : getInitialTemplateVariantKey(template);
  const previousLocalizedPromptTemplates = mergeLocalizedTemplatePromptTexts(instance.localizedPromptTemplates);
  const previousPromptTemplateDefault = resolveTemplatePromptTemplate(
    instance.basePromptTemplate,
    instance.variants,
    instance.selectedVariantKey,
    previousLocalizedPromptTemplates,
  );
  const nextLocalizedPromptTemplates = resolveTemplateLocalizedPromptTemplates(template, nextSelectedVariantKey);
  const nextPromptTemplateDefault = resolveTemplatePromptTemplate(
    template.prompt_template,
    template.variants,
    nextSelectedVariantKey,
    nextLocalizedPromptTemplates,
  );
  const currentPromptTemplate = typeof instance.promptTemplate === 'string' ? instance.promptTemplate : '';
  const currentPromptWasLocalizedDefault = Boolean(
    previousLocalizedPromptTemplates
    && (currentPromptTemplate === previousLocalizedPromptTemplates.en || currentPromptTemplate === previousLocalizedPromptTemplates.zh),
  );
  const nextPromptTemplate = currentPromptTemplate.trim().length === 0 || currentPromptTemplate === previousPromptTemplateDefault || currentPromptWasLocalizedDefault
    ? nextPromptTemplateDefault
    : currentPromptTemplate;

  const nextSlots = getTemplateRuntimeSlots(template.input_slots, template.variants, nextSelectedVariantKey, template.model_params || {});

  nextSlots.forEach((slot) => {
    const nextValue = reconcileTemplateSlotValue(slot, previousSlotMap.get(slot.key), instance.values[slot.key]);
    if (nextValue) {
      nextValues[slot.key] = cloneTemplateSlotValue(nextValue);
    }
  });

  const refreshed: CanvasTemplateInstance = {
    ...instance,
    templateUpdatedAt: templateUpdatedAt,
    name: template.name,
    nameZh: template.name_zh,
    coverUrl: template.cover_url,
    outputType: template.output_type,
    capability: template.capability,
    basePromptTemplate: template.prompt_template,
    promptTemplate: nextPromptTemplate,
    localizedPromptTemplates: nextLocalizedPromptTemplates,
    modelParams: template.model_params || {},
    inputSlots: template.input_slots,
    variants: template.variants,
    selectedVariantKey: nextSelectedVariantKey,
    values: nextValues,
    updatedAt: new Date().toISOString(),
  };

  return {
    instance: normalizeCanvasTemplateInstance(refreshed),
    changed: true,
  };
}

function getVariantModelParams(instance: CanvasTemplateInstance): Record<string, unknown> {
  const variant = getVariantDefinition(instance.variants, instance.selectedVariantKey);
  return (variant?.model_params_override as Record<string, unknown> | undefined) ?? {};
}

function isVisibleSlot(slot: InputSlot, values: CanvasTemplateInstance['values']): boolean {
  if (!slot.visible_when) return true;
  const current = values[slot.visible_when.key]?.selectValue
    || values[slot.visible_when.key]?.text
    || values[slot.visible_when.key]?.sourceUrl;
  if (slot.visible_when.values) {
    return slot.visible_when.values.includes(String(current || ''));
  }
  if (slot.visible_when.value !== undefined) {
    return String(current || '') === String(slot.visible_when.value);
  }
  return Boolean(current);
}

export function getTemplateInstanceSlots(instance: CanvasTemplateInstance): InputSlot[] {
  const slots = getTemplateRuntimeSlots(instance.inputSlots, instance.variants, instance.selectedVariantKey, getTemplateInstanceModelParams(instance));
  return slots.filter(slot => isVisibleSlot(slot, instance.values));
}

export function getTemplateInstanceCapability(instance: CanvasTemplateInstance): string {
  const variant = getVariantDefinition(instance.variants, instance.selectedVariantKey);
  return variant?.capability_override || instance.capability;
}

export function getTemplateInstanceModelParams(instance: CanvasTemplateInstance): Record<string, unknown> {
  return {
    ...(instance.modelParams || {}),
    ...getVariantModelParams(instance),
  };
}

export function getTemplateAutoSplitConfig(template: ImageTemplate | CanvasTemplateInstance): { rows: number; cols: number } | null {
  const modelParams = 'outputType' in template
    ? getTemplateInstanceModelParams(template)
    : (template.model_params || {});
  return normalizeAutoSplitConfig(modelParams.auto_split);
}

export function getTemplateAutoSplitAllowOff(template: ImageTemplate | CanvasTemplateInstance): boolean {
  const modelParams = 'outputType' in template
    ? getTemplateInstanceModelParams(template)
    : (template.model_params || {});
  return modelParams.auto_split_allow_off !== false;
}

export function getTemplateDefaultGridAspectRatio(template: ImageTemplate | CanvasTemplateInstance): string {
  const modelParams = 'outputType' in template
    ? getTemplateInstanceModelParams(template)
    : (template.model_params || {});
  if (typeof modelParams.grid_cell_aspect_ratio === 'string') {
    return modelParams.grid_cell_aspect_ratio.trim() || '1:1';
  }
  if (typeof modelParams.aspect_ratio === 'string') {
    return modelParams.aspect_ratio.trim() || '1:1';
  }
  return '1:1';
}

export function getTemplateGridOptions(template: ImageTemplate | CanvasTemplateInstance): Array<{ rows: number; cols: number } | null> {
  const modelParams = 'outputType' in template
    ? getTemplateInstanceModelParams(template)
    : (template.model_params || {});
  const templateAutoSplit = normalizeAutoSplitConfig(modelParams.auto_split);
  if (!templateAutoSplit) {
    return [];
  }

  const promptBuilder = 'outputType' in template
    ? getTemplatePromptBuilder(template)
    : getPromptBuilderFromModelParams(template.model_params || {});
  const options: Array<{ rows: number; cols: number } | null> = [];

  if (getTemplateAutoSplitAllowOff(template)) {
    options.push(null);
  }

  if (promptBuilder === PRODUCT_STORYBOARD_VIDEO_PROMPT_BUILDER || promptBuilder === MODEL_STORYBOARD_VIDEO_PROMPT_BUILDER) {
    options.push(cloneGridConfig(templateAutoSplit));
    return options;
  }

  const configuredOptions = normalizeAutoSplitOptions(modelParams.auto_split_options);
  const gridOptions = configuredOptions.length > 0 ? configuredOptions : TEMPLATE_GRID_OPTIONS;
  gridOptions.forEach((option) => {
    options.push(cloneGridConfig(option));
  });

  return options;
}

export function getTemplateSelectedGrid(instance: CanvasTemplateInstance): { rows: number; cols: number } | null {
  const templateAutoSplit = getTemplateAutoSplitConfig(instance);
  if (!templateAutoSplit) {
    return null;
  }

  const allowOff = getTemplateAutoSplitAllowOff(instance);

  const currentGrid = normalizeAutoSplitConfig(instance.params.auto_split);
  if (currentGrid) {
    return currentGrid;
  }

  if (instance.params.auto_split === false && allowOff) {
    return null;
  }

  if (allowOff) {
    return null;
  }

  return cloneGridConfig(templateAutoSplit);
}

function applyTemplateInstanceRuntimeDefaults(instance: CanvasTemplateInstance): CanvasTemplateInstance {
  const localizedPromptTemplates = mergeLocalizedTemplatePromptTexts(instance.localizedPromptTemplates);
  const resolvedPromptTemplate = resolveTemplatePromptTemplate(
    instance.basePromptTemplate,
    instance.variants,
    instance.selectedVariantKey,
    localizedPromptTemplates,
  );
  const nextInstance: CanvasTemplateInstance = {
    ...instance,
    basePromptTemplate: typeof instance.basePromptTemplate === 'string'
      ? instance.basePromptTemplate
      : (typeof instance.promptTemplate === 'string' ? instance.promptTemplate : ''),
    promptTemplate: typeof instance.promptTemplate === 'string' && instance.promptTemplate.trim().length > 0
      ? instance.promptTemplate
      : resolvedPromptTemplate,
    promptSourceNodeId: typeof instance.promptSourceNodeId === 'string' && instance.promptSourceNodeId.trim().length > 0
      ? instance.promptSourceNodeId
      : null,
    values: sanitizeTemplateInstanceValues(instance),
  };
  const templateAutoSplit = getTemplateAutoSplitConfig(nextInstance);
  const nextParams: Record<string, unknown> = { ...nextInstance.params };

  if (!templateAutoSplit) {
    delete nextParams.auto_split;
    delete nextParams.grid_cell_aspect_ratio;
    delete nextParams.aspect_ratio;
    return {
      ...nextInstance,
      params: nextParams,
    };
  }

  const currentGrid = normalizeAutoSplitConfig(nextParams.auto_split);
  const allowOff = getTemplateAutoSplitAllowOff(instance);
  const keepOff = nextParams.auto_split === false && allowOff;

  if (currentGrid) {
    nextParams.auto_split = cloneGridConfig(currentGrid);
  } else if (keepOff) {
    nextParams.auto_split = false;
  } else if (allowOff) {
    delete nextParams.auto_split;
  } else {
    nextParams.auto_split = cloneGridConfig(templateAutoSplit);
  }

  if (nextParams.auto_split === false || nextParams.auto_split === undefined) {
    delete nextParams.grid_cell_aspect_ratio;
    delete nextParams.aspect_ratio;
    return {
      ...nextInstance,
      params: nextParams,
    };
  }

  const currentRatio = typeof nextParams.grid_cell_aspect_ratio === 'string'
    ? nextParams.grid_cell_aspect_ratio.trim()
    : '';
  const ratio = currentRatio || getTemplateDefaultGridAspectRatio(nextInstance);
  nextParams.grid_cell_aspect_ratio = ratio;
  nextParams.aspect_ratio = ratio;

  return {
    ...nextInstance,
    params: nextParams,
  };
}

export function normalizeCanvasTemplateInstance(instance: CanvasTemplateInstance): CanvasTemplateInstance {
  const normalized = applyTemplateInstanceRuntimeDefaults(instance);
  return {
    ...normalized,
    outputCount: clampTemplateOutputCount(normalized, normalized.outputCount || 1),
  };
}

export function updateTemplateInstanceSelectedVariant(
  instance: CanvasTemplateInstance,
  selectedVariantKey: string | null,
): CanvasTemplateInstance {
  const localizedPromptTemplates = mergeLocalizedTemplatePromptTexts(instance.localizedPromptTemplates);
  const previousPromptTemplateDefault = resolveTemplatePromptTemplate(
    instance.basePromptTemplate,
    instance.variants,
    instance.selectedVariantKey,
    localizedPromptTemplates,
  );
  const nextSelectedVariantKey = selectedVariantKey
    && instance.variants?.some((variant) => variant.key === selectedVariantKey)
    ? selectedVariantKey
    : null;
  const nextPromptTemplateDefault = resolveTemplatePromptTemplate(
    instance.basePromptTemplate,
    instance.variants,
    nextSelectedVariantKey,
    localizedPromptTemplates,
  );
  const previousSlots = getTemplateRuntimeSlots(instance.inputSlots, instance.variants, instance.selectedVariantKey, getTemplateInstanceModelParams(instance));
  const previousSlotMap = new Map(previousSlots.map((slot) => [slot.key, slot]));
  const nextSlots = getTemplateRuntimeSlots(instance.inputSlots, instance.variants, nextSelectedVariantKey, getTemplateInstanceModelParams(instance));
  const nextValues: CanvasTemplateInstance['values'] = {};
  const currentPromptTemplate = typeof instance.promptTemplate === 'string' ? instance.promptTemplate : '';
  const currentPromptWasLocalizedDefault = Boolean(
    localizedPromptTemplates
    && (currentPromptTemplate === localizedPromptTemplates.en || currentPromptTemplate === localizedPromptTemplates.zh),
  );
  const nextPromptTemplate = currentPromptTemplate.trim().length === 0 || currentPromptTemplate === previousPromptTemplateDefault || currentPromptWasLocalizedDefault
    ? nextPromptTemplateDefault
    : currentPromptTemplate;

  nextSlots.forEach((slot) => {
    const nextValue = reconcileTemplateSlotValue(slot, previousSlotMap.get(slot.key), instance.values[slot.key]);
    if (nextValue) {
      nextValues[slot.key] = cloneTemplateSlotValue(nextValue);
    }
  });

  return normalizeCanvasTemplateInstance({
    ...instance,
    selectedVariantKey: nextSelectedVariantKey,
    promptTemplate: nextPromptTemplate,
    values: nextValues,
    updatedAt: new Date().toISOString(),
  });
}

export function updateTemplateInstanceGrid(
  instance: CanvasTemplateInstance,
  nextGrid: { rows: number; cols: number } | null,
): CanvasTemplateInstance {
  if (!getTemplateAutoSplitConfig(instance)) {
    return instance;
  }

  const nextParams: Record<string, unknown> = { ...instance.params };
  if (nextGrid) {
    const ratio = typeof nextParams.grid_cell_aspect_ratio === 'string'
      ? nextParams.grid_cell_aspect_ratio.trim() || getTemplateDefaultGridAspectRatio(instance)
      : getTemplateDefaultGridAspectRatio(instance);
    nextParams.auto_split = cloneGridConfig(nextGrid);
    nextParams.grid_cell_aspect_ratio = ratio;
    nextParams.aspect_ratio = ratio;
  } else {
    nextParams.auto_split = false;
    delete nextParams.grid_cell_aspect_ratio;
    delete nextParams.aspect_ratio;
  }

  return {
    ...instance,
    params: nextParams,
    updatedAt: new Date().toISOString(),
  };
}

export function updateTemplateInstanceGridAspectRatio(
  instance: CanvasTemplateInstance,
  aspectRatio: string,
): CanvasTemplateInstance {
  const selectedGrid = getTemplateSelectedGrid(instance);
  if (!selectedGrid) {
    return instance;
  }

  const nextRatio = aspectRatio.trim() || getTemplateDefaultGridAspectRatio(instance);
  return {
    ...instance,
    params: {
      ...instance.params,
      auto_split: cloneGridConfig(selectedGrid),
      grid_cell_aspect_ratio: nextRatio,
      aspect_ratio: nextRatio,
    },
    updatedAt: new Date().toISOString(),
  };
}

export function getTemplatePromptBuilder(instance: CanvasTemplateInstance): string {
  return getPromptBuilderFromModelParams(getTemplateInstanceModelParams(instance));
}

export function isStoryboardTemplateInstance(
  instance: Pick<CanvasTemplateInstance, 'capability' | 'modelParams'>,
): boolean {
  const promptBuilder = getPromptBuilderFromModelParams(instance.modelParams || {});
  return instance.capability === BUILTIN_VIDEO_STORYBOARD_TEMPLATE_KIND
    || promptBuilder === PRODUCT_STORYBOARD_VIDEO_PROMPT_BUILDER
    || promptBuilder === MODEL_STORYBOARD_VIDEO_PROMPT_BUILDER;
}

export function isStoryboardVideoStageTemplateInstance(instance: CanvasTemplateInstance): boolean {
  const capability = getTemplateInstanceCapability(instance);
  return capability === PRODUCT_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND
    || capability === MODEL_STORYBOARD_VIDEO_STAGE_TEMPLATE_KIND;
}

export function shouldRouteStoryboardTemplateOutputToStageStoryboardSlot(
  targetInstance: CanvasTemplateInstance,
  sourceInstance: CanvasTemplateInstance | null | undefined,
): boolean {
  if (!sourceInstance) {
    return false;
  }
  return isStoryboardVideoStageTemplateInstance(targetInstance)
    && isStoryboardTemplateInstance(sourceInstance)
    && !isStoryboardVideoStageTemplateInstance(sourceInstance);
}

export function isTemplateCanvasInlineCompatible(_template: ImageTemplate | CanvasTemplateInstance): boolean {
  void _template;
  return true;
}

export function shouldLimitTemplateOutputCountToOne(instance: CanvasTemplateInstance): boolean {
  return instance.outputType === 'video'
    || isStoryboardTemplateInstance(instance)
    || getTemplateInstanceCapability(instance) === 'element_decomposition'
    || Boolean(getBuiltinDesignExtensionPreset(instance.templateId));
}

export function clampTemplateOutputCount(
  instance: CanvasTemplateInstance,
  outputCount: number,
): number {
  if (shouldLimitTemplateOutputCountToOne(instance)) {
    return 1;
  }

  return clampTemplateOutputCountValue(
    Number.isFinite(outputCount) ? Math.round(outputCount) : 1,
    resolveTemplateOutputCountOptions(instance),
  );
}

export function buildTemplateMultiOutputRequestParams(
  instance: CanvasTemplateInstance,
  baseUserParams: Record<string, unknown>,
  outputIndex: number,
  outputTotal: number,
  planGroupId: string,
): Record<string, unknown> {
  const promptBuilder = getTemplatePromptBuilder(instance);
  if (promptBuilder === 'brand_shoot_delivery') {
    return {
      ...baseUserParams,
      brand_shoot_plan_total: outputTotal,
      brand_shoot_plan_index: outputIndex,
      brand_shoot_plan_group_id: planGroupId,
    };
  }

  if (promptBuilder === 'outfit_styling_lookbook_delivery') {
    return {
      ...baseUserParams,
      outfit_styling_plan_total: outputTotal,
      outfit_styling_plan_index: outputIndex,
      outfit_styling_plan_group_id: planGroupId,
    };
  }

  if (promptBuilder === 'white_background_delivery') {
    return {
      ...baseUserParams,
      white_background_plan_total: outputTotal,
      white_background_plan_index: outputIndex,
      white_background_plan_group_id: planGroupId,
    };
  }

  return baseUserParams;
}

export function localizeTemplateSlotLabel(slot: Pick<InputSlot, 'label' | 'label_zh'>, locale: string): string {
  return (locale === 'zh' && slot.label_zh ? slot.label_zh : slot.label) || slot.label_zh || slot.label;
}

export function isTemplateCancellationMessage(message: string | null | undefined): boolean {
  const normalized = message?.trim();
  if (!normalized) {
    return false;
  }

  const lower = normalized.toLowerCase();
  return lower.includes('cancel') || normalized.includes('取消');
}

function getTemplateRunById(
  instance: CanvasTemplateInstance,
  runId: string | null | undefined,
): NonNullable<CanvasTemplateInstance['activeRun']> | null {
  const normalizedRunId = runId?.trim();
  if (!normalizedRunId) {
    return null;
  }

  if (instance.activeRun?.id === normalizedRunId) {
    return instance.activeRun;
  }

  return (instance.runHistory || []).find((run): run is NonNullable<CanvasTemplateInstance['activeRun']> => run?.id === normalizedRunId) || null;
}

function getTemplateRunOutputNodeId(
  instance: CanvasTemplateInstance,
  outputIndex: number,
  preferredRunId?: string | null,
): string | null {
  const seenRunIds = new Set<string>();
  const runs = [
    getTemplateRunById(instance, preferredRunId),
    instance.activeRun,
    ...(instance.runHistory || []),
  ].filter((run): run is NonNullable<CanvasTemplateInstance['activeRun']> => {
    if (!run || seenRunIds.has(run.id)) {
      return false;
    }
    seenRunIds.add(run.id);
    return true;
  });

  for (const run of runs) {
    const outputNodeIds = run.outputNodeIds || (run.outputNodeId ? [run.outputNodeId] : []);
    const outputNodeId = outputNodeIds[outputIndex];
    if (outputNodeId) {
      return outputNodeId;
    }
  }

  return null;
}

function getTemplateRunTaskId(
  instance: CanvasTemplateInstance,
  preferredRunId?: string | null,
  preferredOutputNodeId?: string | null,
): string | null {
  const normalizedOutputNodeId = preferredOutputNodeId?.trim() || '';
  const seenRunIds = new Set<string>();
  const runs = [
    getTemplateRunById(instance, preferredRunId),
    instance.activeRun,
    ...(instance.runHistory || []),
  ].filter((run): run is NonNullable<CanvasTemplateInstance['activeRun']> => {
    if (!run || seenRunIds.has(run.id)) {
      return false;
    }
    seenRunIds.add(run.id);
    return true;
  });

  for (const run of runs) {
    if (normalizedOutputNodeId) {
      const outputNodeIds = run.outputNodeIds || (run.outputNodeId ? [run.outputNodeId] : []);
      if (!outputNodeIds.includes(normalizedOutputNodeId)) {
        continue;
      }
    }

    const taskId = [
      ...(run.taskIds || []),
      run.taskId,
    ].find((candidate): candidate is string => {
      return typeof candidate === 'string' && candidate.trim().length > 0;
    });

    if (taskId) {
      return taskId.trim();
    }
  }

  return null;
}

export function resolveCanvasTemplateSlotMedia(
  value: CanvasTemplateSlotValue | undefined,
  nodes: CanvasNode[],
): ResolvedTemplateSlotMedia | null {
  if (!value) {
    return null;
  }

  if (value.workflowBinding) {
    const explicitOutputNodeId = value.workflowBinding.sourceOutputNodeId?.trim();
    const explicitOutputNode = explicitOutputNodeId
      ? nodes.find((node) => node.id === explicitOutputNodeId)
      : null;
    const explicitSourceUrl = getNodePrimaryMediaUrl(explicitOutputNode);
    if (explicitOutputNode && explicitSourceUrl) {
      return {
        sourceUrl: explicitSourceUrl,
        previewUrl: getNodePreviewImageUrl(explicitOutputNode) || explicitSourceUrl,
        sourceNodeId: explicitOutputNode.id,
        assetId: explicitOutputNode.assetId || null,
        durationSeconds: getNodeDurationSeconds(explicitOutputNode),
      };
    }

    const sourceTemplateNode = nodes.find((node) => node.id === value.workflowBinding?.sourceTemplateNodeId);
    const sourceTemplateInstance = sourceTemplateNode?.templateInstance;
    const outputIndex = Math.max(0, Math.round(value.workflowBinding.outputIndex || 0));
    const outputNodeId = sourceTemplateInstance
      ? getTemplateRunOutputNodeId(sourceTemplateInstance, outputIndex, value.workflowBinding.sourceRunId)
      : null;
    if (outputNodeId) {
      const outputNode = nodes.find((node) => node.id === outputNodeId);
      const sourceUrl = getNodePrimaryMediaUrl(outputNode);
      if (outputNode && sourceUrl) {
        return {
          sourceUrl,
          previewUrl: getNodePreviewImageUrl(outputNode) || sourceUrl,
          sourceNodeId: outputNode.id,
          assetId: outputNode.assetId || null,
          durationSeconds: getNodeDurationSeconds(outputNode),
        };
      }
    }
  }

  if (value.sourceNodeId) {
    const sourceNode = nodes.find((node) => node.id === value.sourceNodeId);
    const sourceUrl = getNodePrimaryMediaUrl(sourceNode);
    if (sourceNode && sourceUrl) {
      return {
        sourceUrl,
        previewUrl: getNodePreviewImageUrl(sourceNode) || sourceUrl,
        sourceNodeId: sourceNode.id,
        assetId: sourceNode.assetId || null,
        durationSeconds: getNodeDurationSeconds(sourceNode),
      };
    }
  }

  const sourceUrl = value.sourceUrl?.trim();
  if (!sourceUrl) {
    return null;
  }

  return {
    sourceUrl,
    previewUrl: value.previewUrl?.trim() || sourceUrl,
    sourceNodeId: value.sourceNodeId,
    assetId: value.assetId,
  };
}

function appendUniqueUrl(urls: string[], url: string | null | undefined) {
  const normalizedUrl = url?.trim();
  if (normalizedUrl && !urls.includes(normalizedUrl)) {
    urls.push(normalizedUrl);
  }
}

function getTemplateRunOutputNodeIdsForSourceLookup(run: CanvasTemplateInstance['activeRun']): string[] {
  if (!run) {
    return [];
  }
  return run.outputNodeIds || (run.outputNodeId ? [run.outputNodeId] : []);
}

function findTemplateNodeForOutputNodeId(nodes: CanvasNode[], outputNodeId: string | null | undefined): CanvasNode | null {
  const normalizedOutputNodeId = outputNodeId?.trim();
  if (!normalizedOutputNodeId) {
    return null;
  }

  return nodes.find((node) => {
    const instance = node.templateInstance;
    if (!instance) {
      return false;
    }
    return [instance.activeRun, ...(instance.runHistory || [])]
      .some((run) => getTemplateRunOutputNodeIdsForSourceLookup(run).includes(normalizedOutputNodeId));
  }) ?? null;
}

function getPrimaryMediaFromNode(node: CanvasNode | null | undefined): ResolvedTemplateSlotMedia | null {
  const sourceUrl = getNodePrimaryMediaUrl(node);
  if (!node || !sourceUrl) {
    return null;
  }
  return {
    sourceUrl,
    previewUrl: getNodePreviewImageUrl(node) || sourceUrl,
    sourceNodeId: node.id,
    assetId: node.assetId || null,
    durationSeconds: getNodeDurationSeconds(node),
  };
}

function isBlockedStageSourceMedia(media: ResolvedTemplateSlotMedia | null, blockedSourceUrls: Set<string>): boolean {
  return !media?.sourceUrl || blockedSourceUrls.has(media.sourceUrl.trim());
}

function isStoryboardSourceTemplateInstance(instance: CanvasTemplateInstance | null | undefined): boolean {
  if (!instance) {
    return false;
  }
  return isStoryboardTemplateInstance(instance)
    && !isStoryboardVideoStageTemplateInstance(instance);
}

function getStoryboardStageSourceSlotKeys(instance: CanvasTemplateInstance): string[] {
  const capability = getTemplateInstanceCapability(instance);
  return STORYBOARD_STAGE_SOURCE_SLOT_KEYS_BY_CAPABILITY[capability] || [
    STORYBOARD_STAGE_SOURCE_SLOT_KEY,
    'product_image',
    'model_image',
    'subject_image',
  ];
}

function resolveSourceImageFromTemplateInstance(
  stageInstance: CanvasTemplateInstance,
  sourceInstance: CanvasTemplateInstance,
  nodes: CanvasNode[],
  blockedSourceUrls: Set<string>,
): ResolvedTemplateSlotMedia | null {
  const imageSlots = getTemplateInstanceSlots(sourceInstance)
    .filter((slot) => slot.type === 'image' && slot.key !== STORYBOARD_STAGE_STORYBOARD_SLOT_KEY);
  const preferredKeys = getStoryboardStageSourceSlotKeys(stageInstance);
  const orderedSlots = [
    ...preferredKeys
      .map((key) => imageSlots.find((slot) => slot.key === key) ?? null)
      .filter((slot): slot is InputSlot => Boolean(slot)),
    ...imageSlots.filter((slot) => slot.required && !preferredKeys.includes(slot.key)),
    ...imageSlots.filter((slot) => !preferredKeys.includes(slot.key)),
  ];
  const seenSlotKeys = new Set<string>();

  for (const slot of orderedSlots) {
    if (seenSlotKeys.has(slot.key)) {
      continue;
    }
    seenSlotKeys.add(slot.key);
    const media = resolveCanvasTemplateSlotMedia(sourceInstance.values[slot.key], nodes);
    if (!isBlockedStageSourceMedia(media, blockedSourceUrls)) {
      return media;
    }
  }

  return null;
}

function getSourceTemplateNodeFromSlotMedia(
  value: CanvasTemplateSlotValue | undefined,
  resolvedMedia: ResolvedTemplateSlotMedia | null,
  nodes: CanvasNode[],
): CanvasNode | null {
  const workflowSourceTemplateNodeId = value?.workflowBinding?.sourceTemplateNodeId?.trim();
  if (workflowSourceTemplateNodeId) {
    const workflowSourceTemplateNode = nodes.find((node) => node.id === workflowSourceTemplateNodeId) ?? null;
    if (workflowSourceTemplateNode?.templateInstance) {
      return workflowSourceTemplateNode;
    }
  }

  return findTemplateNodeForOutputNodeId(nodes, resolvedMedia?.sourceNodeId || value?.sourceNodeId);
}

function isStoryboardStageStoryboardMediaCandidate(
  value: CanvasTemplateSlotValue | undefined,
  resolvedMedia: ResolvedTemplateSlotMedia | null,
  nodes: CanvasNode[],
): boolean {
  const sourceTemplateNode = getSourceTemplateNodeFromSlotMedia(value, resolvedMedia, nodes);
  if (isStoryboardSourceTemplateInstance(sourceTemplateNode?.templateInstance)) {
    return true;
  }

  const sourceNode = resolvedMedia?.sourceNodeId || value?.sourceNodeId
    ? nodes.find((node) => node.id === (resolvedMedia?.sourceNodeId || value?.sourceNodeId)) ?? null
    : null;
  if (!sourceNode) {
    return false;
  }

  if (sourceNode.storyboardData) {
    return true;
  }
  if (
    sourceNode.generationCapability === BUILTIN_VIDEO_STORYBOARD_TEMPLATE_KIND
    || sourceNode.generatingCapability === BUILTIN_VIDEO_STORYBOARD_TEMPLATE_KIND
  ) {
    return true;
  }

  return (sourceNode.lineageSourceNodeIds || []).some((sourceNodeId) => {
    const lineageNode = nodes.find((node) => node.id === sourceNodeId) ?? null;
    return isStoryboardSourceTemplateInstance(lineageNode?.templateInstance);
  });
}

function collectStoryboardSourceTemplateNodeCandidates(
  storyboardValue: CanvasTemplateSlotValue | undefined,
  storyboardMedia: ResolvedTemplateSlotMedia | null,
  nodes: CanvasNode[],
): CanvasNode[] {
  const candidateNodeIds = new Set<string>();
  const workflowSourceTemplateNodeId = storyboardValue?.workflowBinding?.sourceTemplateNodeId?.trim();
  if (workflowSourceTemplateNodeId) {
    candidateNodeIds.add(workflowSourceTemplateNodeId);
  }

  const directSourceNodeId = storyboardMedia?.sourceNodeId || storyboardValue?.sourceNodeId;
  const directSourceNode = directSourceNodeId
    ? nodes.find((node) => node.id === directSourceNodeId) ?? null
    : null;
  const outputSourceTemplateNode = findTemplateNodeForOutputNodeId(nodes, directSourceNodeId);
  if (outputSourceTemplateNode) {
    candidateNodeIds.add(outputSourceTemplateNode.id);
  }

  const storyboardDataTemplateNodeId = typeof directSourceNode?.storyboardData?.template_node_id === 'string'
    ? directSourceNode.storyboardData.template_node_id.trim()
    : '';
  if (storyboardDataTemplateNodeId) {
    candidateNodeIds.add(storyboardDataTemplateNodeId);
  }

  (directSourceNode?.lineageSourceNodeIds || []).forEach((sourceNodeId) => {
    if (sourceNodeId) {
      candidateNodeIds.add(sourceNodeId);
    }
  });

  return [...candidateNodeIds]
    .map((nodeId) => nodes.find((node) => node.id === nodeId) ?? null)
    .filter((node): node is CanvasNode => Boolean(node));
}

function resolveStoryboardStageSourceImageMedia(
  instance: CanvasTemplateInstance,
  nodes: CanvasNode[],
  slots: InputSlot[],
  storyboardValue: CanvasTemplateSlotValue | undefined,
  storyboardMedia: ResolvedTemplateSlotMedia | null,
  promotedStoryboardSlotKey: string | null,
): ResolvedTemplateSlotMedia | null {
  const blockedSourceUrls = new Set<string>();
  if (storyboardMedia?.sourceUrl) {
    blockedSourceUrls.add(storyboardMedia.sourceUrl.trim());
  }

  const preferredCurrentSlotKeys = getStoryboardStageSourceSlotKeys(instance);
  for (const slotKey of preferredCurrentSlotKeys) {
    if (slotKey === STORYBOARD_STAGE_STORYBOARD_SLOT_KEY || slotKey === promotedStoryboardSlotKey) {
      continue;
    }
    const slot = slots.find((candidate) => candidate.key === slotKey && candidate.type === 'image');
    if (!slot) {
      continue;
    }
    const value = instance.values[slot.key];
    const media = resolveCanvasTemplateSlotMedia(value, nodes);
    if (
      !isBlockedStageSourceMedia(media, blockedSourceUrls)
      && !isStoryboardStageStoryboardMediaCandidate(value, media, nodes)
    ) {
      return media;
    }
  }

  for (const candidateNode of collectStoryboardSourceTemplateNodeCandidates(storyboardValue, storyboardMedia, nodes)) {
    if (candidateNode.templateInstance) {
      const media = resolveSourceImageFromTemplateInstance(
        instance,
        candidateNode.templateInstance,
        nodes,
        blockedSourceUrls,
      );
      if (media) {
        return media;
      }
      continue;
    }

    if (candidateNode.mediaType === 'image') {
      const media = getPrimaryMediaFromNode(candidateNode);
      if (!isBlockedStageSourceMedia(media, blockedSourceUrls)) {
        return media;
      }
    }
  }

  return null;
}

function resolveStoryboardVideoStageMedia(
  instance: CanvasTemplateInstance,
  nodes: CanvasNode[],
  slots: InputSlot[],
): StoryboardStageResolvedMedia | null {
  if (!isStoryboardVideoStageTemplateInstance(instance)) {
    return null;
  }

  let storyboardValue = instance.values[STORYBOARD_STAGE_STORYBOARD_SLOT_KEY];
  let storyboardMedia = resolveCanvasTemplateSlotMedia(storyboardValue, nodes);
  let promotedStoryboardSlotKey: string | null = null;

  if (!storyboardMedia?.sourceUrl) {
    const candidateSlots = [
      ...slots.filter((slot) => slot.key === STORYBOARD_STAGE_SOURCE_SLOT_KEY && slot.type === 'image'),
      ...slots.filter((slot) => slot.key !== STORYBOARD_STAGE_STORYBOARD_SLOT_KEY && slot.key !== STORYBOARD_STAGE_SOURCE_SLOT_KEY && slot.type === 'image'),
    ];

    for (const slot of candidateSlots) {
      const value = instance.values[slot.key];
      const media = resolveCanvasTemplateSlotMedia(value, nodes);
      if (media?.sourceUrl && isStoryboardStageStoryboardMediaCandidate(value, media, nodes)) {
        storyboardValue = value;
        storyboardMedia = media;
        promotedStoryboardSlotKey = slot.key;
        break;
      }
    }
  }

  const sourceMedia = resolveStoryboardStageSourceImageMedia(
    instance,
    nodes,
    slots,
    storyboardValue,
    storyboardMedia,
    promotedStoryboardSlotKey,
  );

  return {
    sourceImageUrl: sourceMedia?.sourceUrl,
    storyboardImageUrl: storyboardMedia?.sourceUrl,
    promotedStoryboardSlotKey,
  };
}

function removeMissingSlotByKey(slots: InputSlot[], slotKey: string) {
  for (let index = slots.length - 1; index >= 0; index -= 1) {
    if (slots[index]?.key === slotKey) {
      slots.splice(index, 1);
    }
  }
}

function addMissingSlotByKey(missingSlots: InputSlot[], allSlots: InputSlot[], slotKey: string) {
  if (missingSlots.some((slot) => slot.key === slotKey)) {
    return;
  }
  const slot = allSlots.find((candidate) => candidate.key === slotKey);
  if (slot) {
    missingSlots.push(slot);
  }
}

function addUnresolvedWorkflowBindingSlotByKey(unresolvedSlots: InputSlot[], allSlots: InputSlot[], slotKey: string) {
  if (unresolvedSlots.some((slot) => slot.key === slotKey)) {
    return;
  }
  const slot = allSlots.find((candidate) => candidate.key === slotKey);
  if (slot) {
    unresolvedSlots.push(slot);
  }
}

function addMissingOrUnresolvedWorkflowSlot(params: {
  missingSlots: InputSlot[];
  unresolvedWorkflowBindingSlots: InputSlot[];
  allSlots: InputSlot[];
  slotKey: string;
  value: CanvasTemplateSlotValue | undefined;
  allowUnresolvedWorkflowBindings: boolean;
}) {
  if (params.value?.workflowBinding) {
    removeMissingSlotByKey(params.missingSlots, params.slotKey);
    if (!params.allowUnresolvedWorkflowBindings) {
      addUnresolvedWorkflowBindingSlotByKey(params.unresolvedWorkflowBindingSlots, params.allSlots, params.slotKey);
    }
    return;
  }

  addMissingSlotByKey(params.missingSlots, params.allSlots, params.slotKey);
}

export function validateAndBuildTemplateRunRequest(
  instance: CanvasTemplateInstance,
  nodes: CanvasNode[] = [],
  options?: ValidateTemplateRunRequestOptions,
): TemplateRunValidationResult {
  const slots = getTemplateInstanceSlots(instance);
  const missingSlots: InputSlot[] = [];
  const unresolvedWorkflowBindingSlots: InputSlot[] = [];
  const mediaInputMap: Record<string, string> = {};
  const userImages: string[] = [];
  const userParams: Record<string, unknown> = { ...instance.params };
  let sourceVideoDurationSeconds: number | null = null;

  if (instance.selectedVariantKey) {
    userParams.variant = instance.selectedVariantKey;
  }

  const promptTemplate = (typeof instance.promptTemplate === 'string' ? instance.promptTemplate.trim() : '')
    || localizeCanvasTemplateDefaultPrompt(instance, 'en').trim();
  if (promptTemplate) {
    userParams.prompt_override = promptTemplate;
  }

  for (const slot of slots) {
    const value = instance.values[slot.key];
    if (slot.type === 'image' || slot.type === 'video') {
      const resolvedMedia = resolveCanvasTemplateSlotMedia(value, nodes);
      if (resolvedMedia?.sourceUrl) {
        mediaInputMap[slot.key] = resolvedMedia.sourceUrl;
        appendUniqueUrl(userImages, resolvedMedia.sourceUrl);
        if (
          slot.key === 'source_video'
          && typeof resolvedMedia.durationSeconds === 'number'
          && Number.isFinite(resolvedMedia.durationSeconds)
          && resolvedMedia.durationSeconds > 0
        ) {
          sourceVideoDurationSeconds = resolvedMedia.durationSeconds;
        }
      } else if (options?.allowUnresolvedWorkflowBindings && value?.workflowBinding) {
        continue;
      } else if (value?.workflowBinding) {
        unresolvedWorkflowBindingSlots.push(slot);
      } else if (slot.required) {
        missingSlots.push(slot);
      }
      continue;
    }

    if (slot.type === 'text') {
      const text = value?.text?.trim();
      if (text) {
        userParams[slot.key] = text;
      } else if (slot.required) {
        missingSlots.push(slot);
      }
      continue;
    }

    if (slot.type === 'select') {
      const selectValue = value?.selectValue?.trim();
      if (selectValue) {
        userParams[slot.key] = selectValue;
      } else if (slot.required) {
        missingSlots.push(slot);
      }
    }
  }

  if (sourceVideoDurationSeconds && sourceVideoDurationSeconds > 0) {
    userParams.input_video_duration = sourceVideoDurationSeconds;
  }

  const storyboardStageMedia = resolveStoryboardVideoStageMedia(instance, nodes, slots);
  if (storyboardStageMedia) {
    if (storyboardStageMedia.promotedStoryboardSlotKey) {
      delete mediaInputMap[storyboardStageMedia.promotedStoryboardSlotKey];
    }

    if (storyboardStageMedia.storyboardImageUrl) {
      mediaInputMap[STORYBOARD_STAGE_STORYBOARD_SLOT_KEY] = storyboardStageMedia.storyboardImageUrl;
      appendUniqueUrl(userImages, storyboardStageMedia.storyboardImageUrl);
      removeMissingSlotByKey(missingSlots, STORYBOARD_STAGE_STORYBOARD_SLOT_KEY);
      removeMissingSlotByKey(unresolvedWorkflowBindingSlots, STORYBOARD_STAGE_STORYBOARD_SLOT_KEY);
    } else {
      addMissingOrUnresolvedWorkflowSlot({
        missingSlots,
        unresolvedWorkflowBindingSlots,
        allSlots: slots,
        slotKey: STORYBOARD_STAGE_STORYBOARD_SLOT_KEY,
        value: instance.values[STORYBOARD_STAGE_STORYBOARD_SLOT_KEY],
        allowUnresolvedWorkflowBindings: Boolean(options?.allowUnresolvedWorkflowBindings),
      });
    }

    if (
      storyboardStageMedia.sourceImageUrl
      && storyboardStageMedia.sourceImageUrl !== storyboardStageMedia.storyboardImageUrl
    ) {
      mediaInputMap[STORYBOARD_STAGE_SOURCE_SLOT_KEY] = storyboardStageMedia.sourceImageUrl;
      appendUniqueUrl(userImages, storyboardStageMedia.sourceImageUrl);
      removeMissingSlotByKey(missingSlots, STORYBOARD_STAGE_SOURCE_SLOT_KEY);
      removeMissingSlotByKey(unresolvedWorkflowBindingSlots, STORYBOARD_STAGE_SOURCE_SLOT_KEY);
    } else {
      delete mediaInputMap[STORYBOARD_STAGE_SOURCE_SLOT_KEY];
      addMissingOrUnresolvedWorkflowSlot({
        missingSlots,
        unresolvedWorkflowBindingSlots,
        allSlots: slots,
        slotKey: STORYBOARD_STAGE_SOURCE_SLOT_KEY,
        value: instance.values[STORYBOARD_STAGE_SOURCE_SLOT_KEY],
        allowUnresolvedWorkflowBindings: Boolean(options?.allowUnresolvedWorkflowBindings),
      });
    }
  }

  if (Object.keys(mediaInputMap).length > 0) {
    userParams.media_input_map = mediaInputMap;
  }

  if (missingSlots.length > 0 || unresolvedWorkflowBindingSlots.length > 0) {
    return { ok: false, missingSlots, unresolvedWorkflowBindingSlots };
  }

  return {
    ok: true,
    missingSlots: [],
    unresolvedWorkflowBindingSlots: [],
    request: { userImages, userParams },
  };
}

export function createTemplateOutputPlaceholder(params: {
  nodeId: string;
  position: { x: number; y: number };
  runId: string;
  capability: string;
  generationGroupId: string;
  outputType: 'image' | 'video';
  aspectRatio?: string;
  outputIndex?: number;
  outputCount?: number;
  activityAt?: string;
  sourceLabel?: string;
  chainPending?: boolean;
  workflowAreaGroupId?: string;
}): CanvasNode {
  const sourceLabel = params.sourceLabel || (typeof params.outputIndex === 'number' && (params.outputCount || 1) > 1
    ? `Template Output ${params.outputIndex + 1}`
    : 'Template Output');
  return {
    id: params.nodeId,
    mediaType: params.outputType === 'video' ? 'video' : 'image',
    duration: params.outputType === 'video' ? 0 : 3,
    position: params.position,
    ...(params.aspectRatio ? { aspectRatio: params.aspectRatio } : {}),
    generatingTaskId: `preparing-${params.runId}`,
    generatingCapability: params.capability,
    generationCapability: params.capability,
      ...(!params.chainPending ? { _preparingCreatedAt: Date.now() } : {}),
    ...(params.chainPending ? { _isChainPending: true } : {}),
    generationGroupId: params.generationGroupId,
    ...(params.workflowAreaGroupId ? { workflowAreaGroupId: params.workflowAreaGroupId } : {}),
    lastActivityAt: params.activityAt,
    sourceLabel,
  };
}
